import { differenceInDays, format, set } from 'date-fns';
import omit from 'lodash-es/omit';

import { isNullOrUndefined } from '@app/util/util';
import { EventProperties, SearchProperties } from '@avo/interface/trigger-props';
import { PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { mapFlightClass } from '@search-bar/mappers/url-search.mapper';
import { FlightType } from '@search-bar/entities/flight-type';
import { guestsQuantityMapper, passengersQuantityMapper } from '@flight-results/mappers/flights.mapper';
import { SearchType } from '@search-bar/entities/search-type';
import { Day } from '@shared/base-search-bar/entities/calendar.entities';
import { differenceInDaysMapper } from '@shared/base-search-bar/components/calendar/mappers/calendar-date.mapper';
import {
  HOTEL_SEARCH_DESTINATION_IATA, HOTEL_SEARCH_DESTINATION_ID, HOTEL_SEARCH_DESTINATION_TYPE
} from '@app/modules/hotels/hotel-search/constants/search-props';
import { decodeURIParams } from '@app/mappers/encoding.mapper';
import { isAnInternationalTripBySearch } from '@app/mappers/is-international-trip.mapper';

export function locationNotFoundAmplitudePropsMapper(
  searchType: SearchType, query: string, controlId: string): EventProperties {
  return {
    SEARCH_QUERY: query, CONTROL_ID: controlId, TRAVEL_PRODUCT_TYPE: searchProductTypeMapper(searchType)
  };
}

export function travelDateRestrictedAmplitudePropsMapper(
  searchType: SearchType, day: Day, travelType: string): SearchProperties {
  const producType = searchProductTypeMapper(searchType);
  const addTravelType = producType === SearchBy.FLIGHTS || producType === SearchBy.BUSES;
  return {
    TRAVEL_TRIP_TYPE: addTravelType ? travelType : null,
    ANTICIPACION: differenceInDaysMapper(day),
    TRAVEL_PRODUCT_TYPE: producType
  };
}

export function mapSearchProperties(
  data: SearchData, searchBy: SearchBy, searchId: string | null = null
): SearchProperties {
  if (!data) return null;
  const today = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  let props: EventProperties = getEmptySearchProperties(searchBy);
  props = {
    ...props,
    TRAVEL_PRODUCT_TYPE: searchBy,
    TRAVEL_ADVANCED_PURCHASE: data.departureDate ? differenceInDays(data.departureDate, today) : null,
    TRAVEL_DATE_FROM: data.departureDate ? format(data.departureDate, 'yyyy-MM-dd') : data.departureDate,
    TRAVEL_BRANCH_CODE: data.branchCode.toUpperCase(),
    TRAVEL_DESTINATION_TYPE: amplitudeTravelDestinationType(data, searchBy),
  };
  switch (searchBy) {
    case SearchBy.FLIGHTS:
      props = getFlightSearchProps(props, data);
      break;
    case SearchBy.BUSES:
      props = getBusesSearchProps(props, data);
      break;
    case SearchBy.HOTELS:
      props = getHotelSearchProps(props, data);
      break;
    case SearchBy.BYHOURS:
      props = getHotelByhoursSearchProps(props, data);
      break;
    case SearchBy.PACKAGES:
      props = getPackageSearchProps(props, data);
      break;
    case SearchBy.AUTOS:
      props = getAutoSearchProps(props, data);
      break;
    case SearchBy.ACTIVITIES:
      props = getActivitiesSearchProps(props, data);
      break;
  }
  return definitiveProperties(searchId, props);
}

export function searchProductTypeMapper(searchType: SearchType): SearchBy {
  switch (searchType) {
    case SearchType.FLIGHT_RESULTS:
    case SearchType.HOME_FLIGHT:
      return SearchBy.FLIGHTS;
    case SearchType.BUSES_RESULTS:
    case SearchType.HOME_BUSES:
      return SearchBy.BUSES;
    case SearchType.AUTOS_RESULTS:
    case SearchType.HOME_AUTOS:
      return SearchBy.AUTOS;
    case SearchType.PACKAGES_RESULT:
    case SearchType.PACKAGES_HOME:
      return SearchBy.PACKAGES;
    case SearchType.HOME_BYHOURS:
      return SearchBy.BYHOURS;
    case SearchType.TOUR_AND_ACTIVITIES:
      return SearchBy.ACTIVITIES;
    default:
      return SearchBy.HOTELS;
  }
}

function getEmptySearchProperties(searchBy: SearchBy): SearchProperties {
  const omitProps = getOmitPropsByType(searchBy);
  const props: EventProperties = {
    TRAVEL_PRODUCT_TYPE: '',
    TRAVEL_ADVANCED_PURCHASE: '',
    TRAVEL_DATE_FROM: '',
    TRAVEL_BRANCH_CODE: '',
    TRAVEL_FROM: '',
    TRAVEL_TO: '',
    TRAVEL_TRIP_TYPE: '',
    TRAVEL_PASSENGERS: '',
    TRAVEL_ADULTS: '',
    TRAVEL_CHILDREN: '',
    TRAVEL_INFANTS: '',
    TRAVEL_CLASS_OF_SERVICE: '',
    TRAVEL_WITH_BAGGAGE: null,
    TRAVEL_DATE_TO: '',
    TRAVEL_TRIP_LENGTH: '',
    TRAVEL_ROOMS: '',
    TRAVEL_SEARCH_ID: '',
  };
  return omit(props, omitProps);
}

function getOmitPropsByType(searchBy: SearchBy): string[] {
  switch (searchBy) {
    case SearchBy.AUTOS:
      return [
        'TRAVEL_ADULTS', 'TRAVEL_CHILDREN', 'TRAVEL_INFANTS', 'TRAVEL_PASSENGERS',
        'TRAVEL_CLASS_OF_SERVICE', 'TRAVEL_WITH_BAGGAGE', 'TRAVEL_TRIP_TYPE', 'TRAVEL_ROOMS'
      ];
    case SearchBy.PACKAGES:
      return ['TRAVEL_SEARCH_ID'];
    default:
      return [];
  }
}

function definitiveProperties(searchId: string, props: SearchProperties): SearchProperties {
  if (!isNullOrUndefined(searchId)) {
    return { ...props, TRAVEL_SEARCH_ID: searchId as string };
  } else {
    return props;
  }
}

function amplitudeTravelDestinationType(data: SearchData, type: SearchBy): string {
  const NATIONAL = 'NATIONAL';
  const INTERNATIONAL = 'INTERNATIONAL';
  return isAnInternationalTripBySearch(type, data) ? INTERNATIONAL : NATIONAL;
}

function getFlightSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  const passengers = data.passengerArrangement;
  let props = {
    ...propsBase,
    TRAVEL_FROM: data.departurePlace.code,
    TRAVEL_TO: data.arrivalPlace.code,
    TRAVEL_TRIP_TYPE: data.flightType,
    TRAVEL_PASSENGERS: passengers.reduce((acc, x) => x.quantity + acc, 0),
    TRAVEL_ADULTS: passengersQuantityMapper(passengers, PassengerType.ADULTS),
    TRAVEL_CHILDREN: passengersQuantityMapper(passengers, PassengerType.CHILDREN),
    TRAVEL_INFANTS: passengersQuantityMapper(passengers, PassengerType.INFANTS),
    TRAVEL_CLASS_OF_SERVICE: mapFlightClass(data.flightClass),
    TRAVEL_WITH_BAGGAGE: data.baggageIncluded,
  };
  if (data.flightType === FlightType.ROUNDTRIP) {
    props = {
      ...props,
      TRAVEL_DATE_TO: data.arrivalDate ? format(data.arrivalDate, 'yyyy-MM-dd') : null,
      TRAVEL_TRIP_LENGTH: data.departureDate ? differenceInDays(data.arrivalDate, data.departureDate) : null,
    };
  }
  return props;
}

function getBusesSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  let props = {
    ...propsBase,
    TRAVEL_FROM: data.departureTerminal.code,
    TRAVEL_TO: data.arrivalTerminal.code,
    TRAVEL_TRIP_TYPE: data.flightType,
  };
  if (data.flightType === FlightType.ROUNDTRIP) {
    props = {
      ...props,
      TRAVEL_DATE_TO: data.arrivalDate ? format(data.arrivalDate, 'yyyy-MM-dd') : data.arrivalDate,
      TRAVEL_TRIP_LENGTH: data.departureDate ? differenceInDays(data.arrivalDate, data.departureDate) : null,
    };
  }
  return props;
}

function getHotelSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  const rooms = data.guestsPerRoom;
  const adults = guestsQuantityMapper(rooms, PassengerType.ADULTS);
  const children = guestsQuantityMapper(rooms, PassengerType.CHILDREN);
  return {
    ...propsBase,
    TRAVEL_TO: decodeURIParams(sessionStorage.getItem(HOTEL_SEARCH_DESTINATION_IATA)),
    TRAVEL_DATE_TO: data.arrivalDate ? format(data.arrivalDate, 'yyyy-MM-dd') : data.arrivalDate,
    TRAVEL_TRIP_LENGTH: data.departureDate ? differenceInDays(data.arrivalDate, data.departureDate) : null,
    TRAVEL_ROOMS: rooms.length,
    TRAVEL_PASSENGERS: adults + children,
    TRAVEL_ADULTS: adults,
    TRAVEL_CHILDREN: children,
    TRAVEL_HOTEL_DESTINATION_ID: decodeURIParams(sessionStorage.getItem(HOTEL_SEARCH_DESTINATION_ID)),
    TRAVEL_HOTEL_DESTINATION_TYPE: decodeURIParams(sessionStorage.getItem(HOTEL_SEARCH_DESTINATION_TYPE))
  };
}

function getHotelByhoursSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  return {
    ...getHotelSearchProps(propsBase, data),
    TRAVEL_TRIP_LENGTH: data.hourType[0]
  };
}

function getPackageSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  const packageArrangement = data.passengerArrangement;
  const packageRooms = data.guestsPerRoom;
  return {
    ...propsBase,
    TRAVEL_FROM: data?.packageOriginPlace?.NearestIata,
    TRAVEL_TO: data?.packageDestinationPlace?.NearestIata,
    TRAVEL_TRIP_TYPE: data.flightType,
    TRAVEL_PASSENGERS: packageArrangement.reduce((acc, x) => x.quantity + acc, 0),
    TRAVEL_ADULTS: passengersQuantityMapper(packageArrangement, PassengerType.ADULTS),
    TRAVEL_CHILDREN: passengersQuantityMapper(packageArrangement, PassengerType.CHILDREN),
    TRAVEL_INFANTS: passengersQuantityMapper(packageArrangement, PassengerType.INFANTS),
    TRAVEL_CLASS_OF_SERVICE: mapFlightClass(data.flightClass),
    TRAVEL_WITH_BAGGAGE: data.baggageIncluded,
    TRAVEL_DATE_TO: data.arrivalDate ? format(data.arrivalDate, 'yyyy-MM-dd') : null,
    TRAVEL_TRIP_LENGTH: data.departureDate ? differenceInDays(data.arrivalDate, data.departureDate) : null,
    TRAVEL_ROOMS: packageRooms.length,
  };
}

function getAutoSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  const dateTo = data.arrivalDate ? format(data.arrivalDate, 'yyyy-MM-dd') : data.arrivalDate;
  return {
    ...propsBase,
    TRAVEL_FROM: data.pickUpPlace?.code || data.pickUpPlace?.related_city,
    TRAVEL_TO: data.dropOffPlace?.code || data.dropOffPlace?.related_city,
    TRAVEL_DATE_FROM: `${propsBase.TRAVEL_DATE_FROM} ${data.pickUpTime}`,
    TRAVEL_DATE_TO: `${dateTo} ${data.dropOffTime}`,
    TRAVEL_TRIP_LENGTH: data.departureDate && data.arrivalDate ? differenceInDays(data.arrivalDate, data.departureDate) : null,
  };
}

function getActivitiesSearchProps(propsBase: SearchProperties, data: SearchData): SearchProperties {
  return {
    ...propsBase,
    TRAVEL_TO: data.departurePlace.code,
    TRAVEL_DATE_FROM: null, // Temporal logic until Netactica solution 
  };
}
